/**
 * Utility functions for AI Coach Profile remote API operations.
 */

/**
 * Fetches the AI coach profile markdown for a given coach.
 * @param {string} coachId - The coach ID.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object.
 * @returns {Promise<string>} - The profile markdown text.
 */
export const getCoachProfile = async (coachId, remote) => {
  const endpoint = `coachProfile-getCoachProfile?coachId=${encodeURIComponent(coachId)}`;
  const response = await remote(endpoint, null, { method: 'GET' });
  const result = await response.json();

  if (!response.ok) {
    if (result?.error === 'Profile not found') {
      return '';
    }

    throw new Error(result?.error || 'Failed to fetch coach profile');
  }

  return result?.data?.profileMarkdown || '';
};

/**
 * Generates a new AI coach profile.
 * @param {string} coachId - The coach ID.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object.
 * @returns {Promise<Object>} - The generated profile data.
 */
export const generateCoachProfile = async (coachId, remote) => {
  const response = await remote('coachProfile-generateCoachProfile', {
    coachId,
  }, { method: 'POST' });
  const { data, error } = await response.json();

  if (!response.ok) {
    if (error === 'Not enough interactions to generate profile') {
      throw new Error(error);
    }
    throw new Error('Failed to create coach profile');
  }
  return data.profileMarkdown;
};

/**
 * Updates an existing AI coach profile.
 * @param {string} coachId - The coach ID.
 * @param {string} profileMarkdown - The updated markdown text.
 * @param {firebase.remote.RemoteType} remote - The Firebase remote object.
 * @returns {Promise<Object>} - The updated profile data.
 */
export const updateCoachProfile = async (coachId, profileMarkdown, remote) => {
  const response = await remote('coachProfile-updateCoachProfile', {
    coachId,
    profileMarkdown,
  }, { method: 'PATCH' });
  const { data } = await response.json();
  if (!response.ok) throw new Error('Failed to update coach profile');
  return data.profileMarkdown;
};
